import { useEffect, useMemo, useState } from 'react'
import { Box, Breadcrumbs, Typography, Stack, Button, Chip, IconButton, Menu, MenuItem, ListItemIcon, ListItemText, TextField, InputAdornment, Snackbar, Alert } from '@mui/material'
import { Add, MoreVert, Edit, Block, CheckCircleOutlined, Search, Inventory2Outlined, VerifiedOutlined } from '@mui/icons-material'
import { DataGrid, type GridColDef } from '@mui/x-data-grid'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { listSuppliers, deactivateSupplier, reactivateSupplier, type SupplierListItem } from '../../api/furnizori'
import { errorMessage } from '../../api/client'
import SupplierFormDialog from './SupplierFormDialog'
import SupplierArticlesDialog from './SupplierArticlesDialog'
import SupplierCertificationsDialog from './SupplierCertificationsDialog'

export default function SuppliersPage() {
  const [search, setSearch] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 300)
    return () => clearTimeout(t)
  }, [search])

  const [formTarget, setFormTarget] = useState<SupplierListItem | null | 'new'>(null)
  const [articlesTarget, setArticlesTarget] = useState<SupplierListItem | null>(null)
  const [certsTarget, setCertsTarget] = useState<SupplierListItem | null>(null)
  const [menuAnchor, setMenuAnchor] = useState<{ el: HTMLElement; row: SupplierListItem } | null>(null)
  const [error, setError] = useState<string | null>(null)

  const qc = useQueryClient()
  const { data, isLoading } = useQuery({ queryKey: ['achizitii', 'furnizori', debouncedSearch], queryFn: () => listSuppliers(debouncedSearch) })
  const closeMenu = () => setMenuAnchor(null)
  const invalidate = () => void qc.invalidateQueries({ queryKey: ['achizitii', 'furnizori'] })

  const deactivateMutation = useMutation({ mutationFn: deactivateSupplier, onSuccess: invalidate, onError: (e) => setError(errorMessage(e)) })
  const reactivateMutation = useMutation({ mutationFn: reactivateSupplier, onSuccess: invalidate, onError: (e) => setError(errorMessage(e)) })

  const columns = useMemo<GridColDef<SupplierListItem>[]>(() => [
    { field: 'code', headerName: 'Cod', width: 100, renderCell: (p) => <code>{p.row.code}</code> },
    { field: 'name', headerName: 'Denumire', flex: 1.3, minWidth: 220 },
    { field: 'cui', headerName: 'CUI', width: 130, renderCell: (p) => p.row.cui ?? '—' },
    { field: 'category', headerName: 'Categorie', width: 150, renderCell: (p) => p.row.category ?? '—' },
    { field: 'leadTimeDaysStandard', headerName: 'Timp mediu livrare (zile)', width: 170, renderCell: (p) => p.row.leadTimeDaysStandard ?? '—' },
    {
      field: 'status', headerName: 'Status', width: 100, sortable: false,
      renderCell: (p) => p.row.isActive
        ? <Chip label="Activ" size="small" color="success" variant="outlined" />
        : <Chip label="Inactiv" size="small" variant="outlined" />,
    },
    {
      field: 'actions', headerName: '', width: 56, sortable: false, filterable: false, disableColumnMenu: true,
      renderCell: (p) => (
        <IconButton size="small" onClick={(e) => setMenuAnchor({ el: e.currentTarget, row: p.row })}>
          <MoreVert fontSize="small" />
        </IconButton>
      ),
    },
  ], [])

  return (
    <Stack spacing={2}>
      <Breadcrumbs separator="/" sx={{ fontSize: 12.5 }}>
        <Typography variant="inherit" color="text.secondary">Achiziții</Typography>
        <Typography variant="inherit" color="text.primary">Furnizori</Typography>
      </Breadcrumbs>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1.5 }}>
        <Typography variant="h2">Furnizori</Typography>
        <Button variant="contained" startIcon={<Add />} onClick={() => setFormTarget('new')}>Adaugă furnizor</Button>
      </Box>

      <TextField
        size="small" placeholder="Caută după cod, denumire sau CUI…" value={search}
        onChange={(e) => setSearch(e.target.value)} sx={{ maxWidth: 360 }}
        slotProps={{ input: { startAdornment: <InputAdornment position="start"><Search fontSize="small" /></InputAdornment> } }}
      />

      <Box sx={{ height: 560, bgcolor: 'background.paper', borderRadius: 1.5 }}>
        <DataGrid rows={data ?? []} columns={columns} loading={isLoading} density="comfortable" disableRowSelectionOnClick
          initialState={{ pagination: { paginationModel: { pageSize: 25 } } }} pageSizeOptions={[25, 50]} />
      </Box>

      <SupplierFormDialog supplier={formTarget} onClose={() => setFormTarget(null)} />
      <SupplierArticlesDialog supplier={articlesTarget} onClose={() => setArticlesTarget(null)} />
      <SupplierCertificationsDialog supplier={certsTarget} onClose={() => setCertsTarget(null)} />

      <Menu anchorEl={menuAnchor?.el} open={!!menuAnchor} onClose={closeMenu}>
        {menuAnchor && [
          <MenuItem key="edit" onClick={() => { setFormTarget(menuAnchor.row); closeMenu() }}>
            <ListItemIcon><Edit fontSize="small" /></ListItemIcon><ListItemText>Editare</ListItemText>
          </MenuItem>,
          <MenuItem key="articles" onClick={() => { setArticlesTarget(menuAnchor.row); closeMenu() }}>
            <ListItemIcon><Inventory2Outlined fontSize="small" /></ListItemIcon><ListItemText>Articole furnizate</ListItemText>
          </MenuItem>,
          <MenuItem key="certs" onClick={() => { setCertsTarget(menuAnchor.row); closeMenu() }}>
            <ListItemIcon><VerifiedOutlined fontSize="small" /></ListItemIcon><ListItemText>Certificări</ListItemText>
          </MenuItem>,
          menuAnchor.row.isActive ? (
            <MenuItem key="deactivate" onClick={() => { closeMenu(); deactivateMutation.mutate(menuAnchor.row.id) }}>
              <ListItemIcon><Block fontSize="small" /></ListItemIcon><ListItemText>Dezactivare</ListItemText>
            </MenuItem>
          ) : (
            <MenuItem key="reactivate" onClick={() => { closeMenu(); reactivateMutation.mutate(menuAnchor.row.id) }}>
              <ListItemIcon><CheckCircleOutlined fontSize="small" /></ListItemIcon><ListItemText>Reactivare</ListItemText>
            </MenuItem>
          ),
        ]}
      </Menu>

      <Snackbar open={!!error} autoHideDuration={5000} onClose={() => setError(null)}>
        <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>
      </Snackbar>
    </Stack>
  )
}
